/**
 * Profitability maths for the quote calculator in the Cost Summary.
 *
 * Profit is expressed as a **margin on revenue**: a 40% profit on a cost of
 * 1,00,000 means quoting 1,66,666.67 and keeping 66,666.67. (The alternative
 * reading - markup on cost, where 40% would mean quoting 1,40,000 - is
 * deliberately not what this uses.)
 *
 *     percent = (revenue - cost) / revenue x 100
 *     revenue = cost / (1 - percent / 100)
 *
 * Quoting below cost is legitimate, so the percentage may be negative.
 */

export const DEFAULT_PROFIT_PERCENT = 40;

/**
 * What to quote for `percent` of the revenue to be profit.
 *
 * A margin of 100% would need infinite revenue, and anything above it would
 * need negative revenue, so those return 0 rather than Infinity or a nonsense
 * quote. Below 100% - including negative margins - is a normal quote.
 */
export const revenueFromProfitPercent = (cost: number, percent: number): number => {
  if (!Number.isFinite(cost) || !Number.isFinite(percent)) return 0;
  if (percent >= 100) return 0;
  return cost / (1 - percent / 100);
};

/**
 * The profit implied by quoting `revenue` against `cost`.
 *
 * Revenue of zero has no defined percentage (the margin divides by it), so it
 * returns 0 rather than Infinity.
 */
export const profitPercentFromRevenue = (cost: number, revenue: number): number => {
  if (!Number.isFinite(cost) || !Number.isFinite(revenue) || revenue <= 0) return 0;
  return ((revenue - cost) / revenue) * 100;
};

/** Absolute profit in rupees - may be negative when quoting below cost. */
export const profitAmount = (cost: number, revenue: number): number => {
  if (!Number.isFinite(cost) || !Number.isFinite(revenue)) return 0;
  return revenue - cost;
};

/** Round to 2dp for display and storage, avoiding 39.99999999 artefacts. */
export const round2 = (value: number): number =>
  Number.isFinite(value) ? Math.round(value * 100) / 100 : 0;

/**
 * Indian digit grouping with two decimals: 182875 -> "1,82,875.00".
 *
 * The rupee symbol is rendered as an adornment beside the input rather than
 * baked into the string, so the text stays parseable by parseAmount().
 */
export const formatAmount = (value: number): string =>
  Number.isFinite(value)
    ? value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '';

/**
 * Read a typed amount back to a number, tolerating grouping separators,
 * a rupee symbol and stray spaces. Unparseable input reads as 0.
 */
export const parseAmount = (text: string): number => {
  const cleaned = (text || '').replace(/[^0-9.-]/g, '');
  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Group a half-typed amount so commas appear as the user types.
 *
 * Unlike formatAmount() this never forces ".00" - a trailing "." or a single
 * decimal digit is preserved exactly as typed, so "12345." stays "12,345."
 * instead of jumping to "12,345.00" mid-entry. Decimals are capped at two.
 */
export const formatAmountInput = (raw: string): string => {
  if (!raw) return '';

  const negative = raw.trimStart().startsWith('-');
  const cleaned = raw.replace(/[^0-9.]/g, '');
  if (!cleaned) return negative ? '-' : '';

  const firstDot = cleaned.indexOf('.');
  const hasDecimal = firstDot !== -1;
  const digitsOnly = hasDecimal ? cleaned.slice(0, firstDot) : cleaned;
  // Everything after the first dot, with any further dots dropped.
  const decimals = hasDecimal ? cleaned.slice(firstDot + 1).replace(/\./g, '').slice(0, 2) : '';

  const trimmedInt = digitsOnly.replace(/^0+(?=\d)/, '');
  const groupedInt = trimmedInt === '' ? '' : Number(trimmedInt).toLocaleString('en-IN');

  let out = groupedInt;
  if (hasDecimal) {
    out = `${groupedInt === '' ? '0' : groupedInt}.${decimals}`;
  }
  return negative ? `-${out}` : out;
};

/**
 * Where the caret should sit after reformatting.
 *
 * Grouping separators shift every character to the right of an edit, so the
 * caret is tracked by how many meaningful characters (digits, dot, minus)
 * precede it rather than by raw offset.
 */
export const caretAfterFormat = (formatted: string, significantBefore: number): number => {
  let seen = 0;
  let index = 0;
  while (index < formatted.length && seen < significantBefore) {
    if (/[0-9.-]/.test(formatted[index])) seen += 1;
    index += 1;
  }
  return index;
};

/** Count digits/dot/minus in the portion of a string before the caret. */
export const significantBeforeCaret = (raw: string, caret: number): number =>
  raw.slice(0, caret).replace(/[^0-9.-]/g, '').length;
